/**
 * Verifiable enrollments via the bigquery-query bot.
 * The UI sends queryName and, for delegated views, practitionerId. The bot binds
 * NPI from the authenticated or authorized Practitioner (or their supervisor when
 * requires-cosignature is true). The browser never sends an NPI.
 */

import { ContentType } from '@medplum/core';
import type { MedplumClient } from '@medplum/core';
import type { Identifier } from '@medplum/fhirtypes';
import { z } from 'zod';

export const BIGQUERY_QUERY_BOT_IDENTIFIER = {
  system: 'https://unify.health/bots',
  value: 'bigquery-query',
} as const;

export const PROVIDER_ENROLLMENTS_QUERY_NAME = 'provider-enrollments';
export const ELIGIBLE_ENROLLMENT_PRACTITIONERS_QUERY_NAME =
  'eligible-enrollment-practitioners';

export const ENROLLMENTS_EMPTY_MESSAGE = 'No enrollment records found.';

export function getBigQueryQueryBotTarget(): string | Identifier {
  const id = import.meta.env.VITE_MEDPLUM_BIGQUERY_QUERY_BOT_ID?.trim();
  if (id) return id;
  const system =
    import.meta.env.VITE_MEDPLUM_BIGQUERY_QUERY_BOT_IDENTIFIER_SYSTEM?.trim() ??
    BIGQUERY_QUERY_BOT_IDENTIFIER.system;
  const value =
    import.meta.env.VITE_MEDPLUM_BIGQUERY_QUERY_BOT_IDENTIFIER_VALUE?.trim() ??
    BIGQUERY_QUERY_BOT_IDENTIFIER.value;
  return { system, value };
}

const nullableText = z.preprocess((value) => {
  if (value == null || value === '') return null;
  const text = String(value).trim();
  return text === '' ? null : text;
}, z.string().nullable());

export const providerEnrollmentRowSchema = z.object({
  payer_name: nullableText,
  payer_state: nullableText,
  enrollment_status: nullableText,
  approval_date: nullableText,
});

const billingThroughSchema = z
  .object({ display: z.string().trim().min(1) })
  .optional();

const USPS_STATE_CODES = new Set([
  'AL', 'AK', 'AS', 'AZ', 'AR', 'CA', 'CO', 'CT', 'DE', 'DC', 'FL', 'GA', 'GU', 'HI', 'ID',
  'IL', 'IN', 'IA', 'KS', 'KY', 'LA', 'ME', 'MD', 'MA', 'MI', 'MN', 'MS', 'MO', 'MP', 'MT',
  'NE', 'NV', 'NH', 'NJ', 'NM', 'NY', 'NC', 'ND', 'OH', 'OK', 'OR', 'PA', 'PR', 'RI', 'SC',
  'SD', 'TN', 'TX', 'UT', 'VT', 'VA', 'VI', 'WA', 'WV', 'WI', 'WY',
]);

/** Trailing Salesforce-style state: dash/slash/pipe, or parenthesized code; not a bare last word. */
const TRAILING_STATE_SUFFIX = /(?:[\s]*[-–—|/][\s]*|\s*(?=\())\(?([A-Za-z]{2})\)?[\s.,;]*$/;

export type ProviderEnrollmentRow = z.infer<typeof providerEnrollmentRowSchema> & { id: string };

export function stripTrailingPayerStateLabel(name: string | null): {
  name: string | null;
  suffixState: string | undefined;
} {
  if (!name) return { name, suffixState: undefined };
  const match = name.match(TRAILING_STATE_SUFFIX);
  if (!match) return { name, suffixState: undefined };
  const code = match[1].toUpperCase();
  if (!USPS_STATE_CODES.has(code)) return { name, suffixState: undefined };
  const stripped = name.slice(0, match.index).trim();
  if (!stripped) return { name, suffixState: undefined };
  return { name: stripped, suffixState: code };
}

export interface ProviderEnrollmentsResult {
  rows: ProviderEnrollmentRow[];
  billingThroughDisplay?: string;
}

const eligibleEnrollmentPractitionerSchema = z.object({
  practitionerId: z.string().trim().min(1),
  display: z.string().trim().min(1),
  npi: z.string().trim().min(1).optional(),
});

const eligibleEnrollmentPractitionersPageSchema = z.object({
  rows: z.array(eligibleEnrollmentPractitionerSchema),
  nextOffset: z.number().int().nonnegative().optional(),
});

export type EligibleEnrollmentPractitioner = z.infer<
  typeof eligibleEnrollmentPractitionerSchema
>;

export function parseProviderEnrollmentsResult(raw: unknown): ProviderEnrollmentsResult {
  if (raw == null || typeof raw !== 'object' || Array.isArray(raw)) {
    throw new Error('Enrollment records could not be loaded.');
  }
  const payload = raw as { rows?: unknown; billingThrough?: unknown };
  if (!Array.isArray(payload.rows)) {
    throw new Error('Enrollment records could not be loaded.');
  }

  const parsedRows = z.array(providerEnrollmentRowSchema).safeParse(payload.rows);
  if (!parsedRows.success) {
    throw new Error('Enrollment records could not be loaded.');
  }

  const billingParsed = billingThroughSchema.safeParse(payload.billingThrough);
  const display = billingParsed.success ? billingParsed.data?.display : undefined;

  return {
    rows: parsedRows.data.map((row, index) => {
      const { name, suffixState } = stripTrailingPayerStateLabel(row.payer_name);
      return {
        ...row,
        id: String(index),
        payer_name: name,
        payer_state: suffixState ?? row.payer_state,
      };
    }),
    billingThroughDisplay: display,
  };
}

export async function fetchProviderEnrollments(
  medplum: MedplumClient,
  practitionerId?: string,
): Promise<ProviderEnrollmentsResult> {
  const result = await medplum.executeBot(
    getBigQueryQueryBotTarget(),
    {
      queryName: PROVIDER_ENROLLMENTS_QUERY_NAME,
      ...(practitionerId ? { practitionerId } : {}),
    },
    ContentType.JSON,
  );
  return parseProviderEnrollmentsResult(result);
}

export async function fetchEligibleEnrollmentPractitioners(
  medplum: MedplumClient,
  search?: string,
): Promise<EligibleEnrollmentPractitioner[]> {
  const rows: EligibleEnrollmentPractitioner[] = [];
  const seenOffsets = new Set<number>();
  let offset: number | undefined;
  const normalizedSearch = search?.trim();

  for (;;) {
    const raw = await medplum.executeBot(
      getBigQueryQueryBotTarget(),
      {
        queryName: ELIGIBLE_ENROLLMENT_PRACTITIONERS_QUERY_NAME,
        ...(normalizedSearch ? { search: normalizedSearch } : {}),
        ...(offset !== undefined ? { offset } : {}),
      },
      ContentType.JSON,
    );
    const parsed = eligibleEnrollmentPractitionersPageSchema.safeParse(raw);
    if (!parsed.success) {
      throw new Error('Practitioner options could not be loaded.');
    }
    rows.push(...parsed.data.rows);
    const nextOffset = parsed.data.nextOffset;
    if (nextOffset === undefined) break;
    if (seenOffsets.has(nextOffset)) {
      throw new Error('Practitioner options could not be loaded.');
    }
    seenOffsets.add(nextOffset);
    offset = nextOffset;
  }

  return rows.sort(
    (a, b) =>
      a.display.localeCompare(b.display) ||
      a.practitionerId.localeCompare(b.practitionerId),
  );
}

/**
 * Label every option with its NPI. Distinct practitioners can legitimately share
 * a name, so options without an NPI fall back to an id prefix to stay distinct.
 */
export function enrollmentPractitionerOptions(
  candidates: EligibleEnrollmentPractitioner[],
): Array<{ value: string; label: string }> {
  const nameCounts = new Map<string, number>();
  for (const candidate of candidates) {
    nameCounts.set(candidate.display, (nameCounts.get(candidate.display) ?? 0) + 1);
  }

  return candidates.map((candidate) => {
    const collides = (nameCounts.get(candidate.display) ?? 0) > 1;
    const detail = candidate.npi ?? (collides ? candidate.practitionerId.slice(0, 8) : undefined);
    return {
      value: candidate.practitionerId,
      label: detail ? `${candidate.display} (${detail})` : candidate.display,
    };
  });
}

export function enrollmentStatusBadgeColor(status: string | null): string {
  switch (status) {
    case 'Enrolled':
    case 'Opted Out':
      return 'green';
    case 'Submitted':
    case 'Not Submitted':
      return 'yellow';
    case 'Denied':
      return 'red';
    default:
      return 'gray';
  }
}

export function enrollmentRowKey(row: ProviderEnrollmentRow): string {
  return row.id;
}

export function billingThroughBannerText(display: string | undefined): string | undefined {
  const trimmed = display?.trim();
  return trimmed ? `Billing through ${trimmed}` : undefined;
}

export function uniquePayerStates(rows: ProviderEnrollmentRow[]): string[] {
  const states = new Set<string>();
  for (const row of rows) {
    const state = row.payer_state?.trim();
    if (state) states.add(state);
  }
  return [...states].sort((a, b) => a.localeCompare(b));
}
